<template>
  <div
    class="relative flex items-start min-h-screen w-full max-w-full overflow-x-clip bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950"
  >
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :is-open-mobile="isSidebarOpenMobile"
      :sidebar-search="sidebarSearch"
      :lang="lang"
      :t="t"
      @update:sidebar-search="sidebarSearch = $event"
      @update:is-open-mobile="isSidebarOpenMobile = $event"
    />



    <div class="flex flex-col flex-1 min-w-0 min-h-0 w-full">
      <AdminTopbar
        :is-collapsed="isSidebarCollapsed"
        :lang="lang"
        :t="t"
        @toggle-sidebar="toggleSidebar"
      />

      <main class="flex-1 px-4 py-4 md:px-6 lg:px-8 min-h-0 overflow-hidden">
        <div
          class="w-full mx-auto max-w-6xl flex flex-col min-h-0 h-full max-h-[calc(100dvh-4.5rem)]"
        >
          <!-- Unified negotiation panel: summary + history side by side -->
          <section
            class="flex flex-col flex-1 min-h-0 overflow-hidden border shadow-sm rounded-2xl bg-white dark:bg-slate-900 border-slate-200/80 dark:border-slate-700/80"
          >
            <!-- Title row -->
            <div
              class="flex items-center gap-3 px-4 py-3 shrink-0 border-b border-slate-100 dark:border-slate-800 min-w-0"
            >
              <div
                v-if="!consultationReady"
                class="flex flex-1 items-center gap-2 min-w-0"
                role="status"
                :aria-label="t('loading') || 'Loading'"
              >
                <span
                  class="h-4 flex-1 max-w-[12rem] rounded bg-slate-200 dark:bg-slate-700 animate-pulse"
                ></span>
                <span
                  class="h-4 w-14 rounded bg-slate-100 dark:bg-slate-800 animate-pulse shrink-0"
                ></span>
              </div>

              <template v-else>
              <div
                class="flex items-center gap-1.5 min-w-0 flex-1"
              >
                <h1
                  class="min-w-0 truncate text-sm font-bold leading-snug text-slate-900 sm:text-base dark:text-white text-start"
                >
                  {{
                    consultation.subject ||
                    t("negotiation_title") ||
                    "Negotiation"
                  }}
                </h1>

                <span
                  v-if="consultation.number || id"
                  class="shrink-0 px-1.5 py-0.5 text-[10px] font-mono rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"
                >
                  #{{ consultation.number || id }}
                </span>

                <span
                  v-if="turnBadgeLabel"
                  class="inline-flex shrink-0 items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-lg bg-secondary/15 text-secondary dark:text-secondary-light"
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full shrink-0 bg-secondary"
                  ></span>
                  <span class="whitespace-nowrap">{{ turnBadgeLabel }}</span>
                </span>
              </div>
              </template>

              <button
                type="button"
                class="inline-flex items-center justify-center w-9 h-9 rounded-lg shrink-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                :aria-label="t('back') || 'Back'"
                @click="goBackToDetails"
              >
                <i class="mdi mdi-close text-[20px]"></i>
              </button>
            </div>

            <div class="flex flex-col flex-1 min-h-0 lg:flex-row">
              <!-- Summary sidebar -->
              <aside
                class="shrink-0 px-4 py-3 space-y-3 border-b lg:w-72 lg:border-b-0 lg:border-e border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30"
              >
                <NegotiationSummarySkeleton
                  v-if="!consultationReady"
                  :aria-label="t('loading') || 'Loading'"
                />

                <template v-else>
                <div
                  class="px-3 py-3 text-center rounded-xl border bg-gradient-to-b from-secondary/10 to-primary/5 dark:from-secondary/15 dark:to-primary/10 border-secondary/20 dark:border-secondary/25"
                >
                  <p class="text-[10px] font-medium text-slate-600 dark:text-slate-300">
                    {{ t("negotiation_last_price") || "Last offered price" }}
                  </p>
                  <p class="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                    {{ formatAmount(currentOffer) }}
                    <span class="text-sm font-semibold text-slate-600 dark:text-slate-300">
                      {{ t("currency_sar") || "SAR" }}
                    </span>
                    <span
                      v-if="latestOfferAuthorLabel"
                      class="text-xs font-medium text-slate-500 dark:text-slate-400"
                    >
                      ({{ latestOfferAuthorLabel }})
                    </span>
                  </p>
                </div>

                <div
                  v-if="isNegotiationOpen || negotiationOfferCount > 0"
                  class="flex flex-col items-center gap-2"
                >
                  <div
                    class="flex items-center justify-center gap-0"
                    :dir="lang === 'ar' ? 'rtl' : 'ltr'"
                  >
                    <template
                      v-for="(step, idx) in NEGOTIATION_ROUND_STEPS"
                      :key="step"
                    >
                      <div
                        class="flex items-center justify-center w-8 h-8 text-xs font-bold rounded-full transition-colors"
                        :class="roundStepClass(step)"
                      >
                        {{ step }}
                      </div>
                      <div
                        v-if="idx < NEGOTIATION_ROUND_STEPS.length - 1"
                        class="w-8 sm:w-10 h-0.5 shrink-0"
                        :class="
                          step < currentNegotiationRound
                            ? 'bg-primary'
                            : 'bg-slate-200 dark:bg-slate-700'
                        "
                      ></div>
                    </template>
                  </div>
                  <p
                    v-if="isNegotiationOpen"
                    class="text-[10px] text-slate-500 dark:text-slate-400"
                  >
                    {{ t("negotiation_round_of") || "Round" }}
                    <span class="font-semibold text-slate-700 dark:text-slate-200">
                      {{ currentNegotiationRound }} / {{ MAX_NEGOTIATION_ROUNDS }}
                    </span>
                  </p>
                </div>

                <div
                  v-if="isNegotiationOpen && !hasReachedMaxOffers && !showFinalRoundWarning && currentNegotiationRound < MAX_NEGOTIATION_ROUNDS"
                  class="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-primary/20 bg-primary/5 dark:bg-primary/10 dark:border-primary/25"
                >
                  <span
                    class="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-full border border-primary/30 bg-white/70 dark:bg-slate-900/40"
                  >
                    <i
                      class="mdi mdi-information-outline text-primary text-[16px]"
                    ></i>
                  </span>
                  <p
                    class="min-w-0 text-[10px] leading-relaxed text-slate-700 dark:text-slate-200"
                  >
                    {{ negotiationLimitNote }}
                  </p>
                </div>

                <div
                  v-if="showFinalRoundWarning"
                  class="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-amber-200/80 bg-amber-50 dark:bg-amber-900/25 dark:border-amber-800/50"
                >
                  <span
                    class="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-full border border-amber-300/60 dark:border-amber-700/50 bg-white/70 dark:bg-slate-900/40"
                  >
                    <i
                      class="mdi mdi-alert-outline text-amber-600 dark:text-amber-400 text-[16px]"
                    ></i>
                  </span>
                  <p
                    class="min-w-0 text-[10px] leading-relaxed text-amber-900 dark:text-amber-100"
                  >
                    {{ negotiationFinalRoundWarning }}
                  </p>
                </div>

                <div
                  v-if="isNegotiationOpen && isWaitingForOtherParty"
                  class="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-amber-100 dark:border-amber-900/40 bg-amber-50/90 dark:bg-amber-900/20"
                >
                  <span
                    class="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-full border border-amber-300/60 dark:border-amber-700/50 bg-white/70 dark:bg-slate-900/40"
                  >
                    <i
                      class="mdi mdi-timer-sand text-amber-600 dark:text-amber-400 text-[16px]"
                    ></i>
                  </span>
                  <p
                    class="min-w-0 text-[10px] leading-relaxed text-slate-700 dark:text-slate-200"
                  >
                    {{
                      t("negotiation_waiting_other") ||
                      "Waiting for the other party to respond."
                    }}
                  </p>
                </div>

                <div
                  v-if="isNegotiationSettled"
                  class="rounded-lg border px-2.5 py-2 bg-[#FCF8F1] border-secondary/35 dark:bg-secondary/10 dark:border-secondary/40"
                >
                  <div class="flex items-center gap-2">
                    <span
                      class="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-full border border-secondary/45 bg-white/70 dark:bg-slate-900/40"
                    >
                      <i
                        class="mdi mdi-check-circle-outline text-secondary dark:text-secondary-light text-[16px]"
                      ></i>
                    </span>
                    <p
                      class="min-w-0 text-[10px] leading-relaxed text-secondary dark:text-secondary-light"
                    >
                      {{ t("negotiation_agreed") || "Agreed on" }}
                      <span class="font-bold tabular-nums whitespace-nowrap">
                        ({{ formatAmount(currentOffer) }}
                        {{ t("currency_sar") || "SAR" }})
                      </span>
                    </p>
                  </div>
                  <p
                    v-if="latestOffer?.note"
                    class="mt-2 pt-2 text-[10px] leading-relaxed break-words whitespace-pre-wrap text-secondary/85 dark:text-secondary-light/85 border-t border-secondary/20"
                    :class="lang === 'ar' ? 'text-end' : 'text-start'"
                  >
                    {{ latestOffer.note }}
                  </p>
                </div>

                <button
                  v-if="canShowOfferButton"
                  type="button"
                  class="inline-flex items-center justify-center gap-1.5 w-full px-3 py-2 text-xs font-semibold text-white rounded-lg shadow-sm bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60 disabled:pointer-events-none"
                  :disabled="!consultationLoaded || isSubmitting"
                  @click="openOfferModal"
                >
                  <i class="mdi mdi-file-document-edit-outline text-[15px]"></i>
                  {{ offerCtaLabel }}
                </button>

                <div
                  v-else-if="showMaxOffersHint"
                  class="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-secondary/35 bg-[#FCF8F1] dark:bg-secondary/10 dark:border-secondary/40"
                >
                  <span
                    class="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-full border border-secondary/45 bg-white/70 dark:bg-slate-900/40"
                  >
                    <i
                      class="mdi mdi-lock-check-outline text-secondary dark:text-secondary-light text-[16px]"
                    ></i>
                  </span>
                  <p
                    class="min-w-0 text-[10px] leading-relaxed text-secondary dark:text-secondary-light"
                  >
                    {{
                      t("negotiation_max_offers_hint") ||
                      "Maximum offers reached. Please accept or reject the latest offer."
                    }}
                  </p>
                </div>
                </template>
              </aside>

              <!-- History + actions -->
              <div class="flex flex-col flex-1 min-h-0 min-w-0">
                <header
                  class="flex items-center gap-2 px-4 py-2.5 shrink-0 border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
                >
                  <i class="mdi mdi-history text-primary text-[16px]"></i>
                  <span class="text-xs font-semibold text-slate-800 dark:text-slate-100">
                    {{ t("negotiation_history") || "Financial negotiation log" }}
                  </span>
                  <span
                    v-if="negotiationOfferCount > 0"
                    class="ms-auto text-[10px] font-medium px-2 py-0.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500"
                  >
                    {{ negotiationOfferCount }}
                  </span>
                  <span
                    v-else-if="!hasHistoryLoadedOnce"
                    class="ms-auto inline-flex items-center gap-1 text-[10px] text-slate-400"
                  >
                    <i class="mdi mdi-loading mdi-spin text-[12px]"></i>
                  </span>
                </header>

                <div
                  ref="historyScrollRef"
                  class="relative flex-1 min-h-0 px-4 py-2 overflow-y-auto custom-scrollbar"
                >
              <NegotiationHistorySkeleton
                v-if="!hasHistoryLoadedOnce || isHistoryLoading"
                :count="historySkeletonCount"
                :show-round-header="negotiationOfferCount > 0"
                :aria-label="t('loading') || 'Loading'"
              />

              <div
                v-else-if="loadError"
                class="flex flex-col items-center justify-center gap-2 py-10 text-center"
              >
                <span
                  class="flex items-center justify-center w-10 h-10 rounded-full bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300"
                >
                  <i class="mdi mdi-alert-circle-outline text-[20px]"></i>
                </span>
                <p class="text-sm text-rose-600 dark:text-rose-300">
                  {{ loadError }}
                </p>
                <button
                  type="button"
                  class="inline-flex items-center gap-1 px-3 py-1.5 mt-1 text-xs font-semibold rounded-lg text-primary bg-primary/10 hover:bg-primary/15 transition-colors"
                  @click="retryLoadHistory"
                >
                  <i class="mdi mdi-refresh text-[14px]"></i>
                  {{ t("retry") || "Retry" }}
                </button>
              </div>

              <div
                v-else-if="!negotiations.length"
                class="flex flex-col items-center justify-center gap-2 py-10 text-center"
              >
                <span
                  class="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400"
                >
                  <i class="mdi mdi-handshake-outline text-[20px]"></i>
                </span>
                <p class="text-sm text-slate-500 dark:text-slate-400">
                  {{ t("negotiation_empty") || "No negotiations yet." }}
                </p>
              </div>

              <div v-else class="space-y-3">
                <section
                  v-for="group in negotiationRoundGroups"
                  :key="`round-${group.round}`"
                  class="space-y-2"
                >
                  <div
                    class="flex items-center gap-2 px-1"
                    :class="
                      group.round === currentNegotiationRound &&
                      isNegotiationOpen
                        ? 'text-primary'
                        : 'text-slate-500 dark:text-slate-400'
                    "
                  >
                    <span
                      class="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full shrink-0"
                      :class="
                        group.round === currentNegotiationRound &&
                        isNegotiationOpen
                          ? 'bg-primary text-white'
                          : group.round < currentNegotiationRound
                            ? 'bg-primary/15 text-primary border border-primary/25'
                            : 'bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700'
                      "
                    >
                      {{ group.round }}
                    </span>
                    <span class="text-[10px] font-semibold tracking-wide">
                      {{ formatRoundLabel(group.round) }}
                    </span>
                    <span class="text-[10px] opacity-70">
                      / {{ MAX_NEGOTIATION_ROUNDS }}
                    </span>
                    <div
                      class="flex-1 h-px"
                      :class="
                        group.round === currentNegotiationRound &&
                        isNegotiationOpen
                          ? 'bg-primary/30'
                          : 'bg-slate-200 dark:bg-slate-700'
                      "
                    ></div>
                  </div>

                  <ul class="space-y-2">
                    <li
                      v-for="row in group.offers"
                      :key="row.id"
                      class="rounded-lg border transition-colors"
                      :class="
                        row.isMine
                          ? 'border-primary/20 bg-primary/5 dark:bg-primary/10'
                          : 'border-secondary/20 bg-secondary/5 dark:bg-secondary/10 dark:border-secondary/25'
                      "
                    >
                      <div class="flex items-start gap-2.5 px-3 py-2">
                        <span
                          class="inline-flex items-center justify-center w-7 h-7 rounded-full shrink-0"
                          :class="
                            row.isMine
                              ? 'bg-primary/10 text-primary'
                              : 'bg-secondary/15 text-secondary dark:text-secondary-light'
                          "
                        >
                          <i class="mdi mdi-account text-[14px]"></i>
                        </span>

                        <div class="min-w-0 flex-1">
                          <div class="flex items-start justify-between gap-3">
                            <span
                              class="min-w-0 truncate text-[11px] font-semibold leading-tight"
                              :class="
                                row.isMine
                                  ? 'text-primary'
                                  : 'text-secondary dark:text-secondary-light'
                              "
                            >
                              {{
                                row.isMine
                                  ? t("you") || "You"
                                  : row.senderName
                              }}
                            </span>

                            <div class="text-end shrink-0">
                              <div
                                class="text-[11px] font-bold tabular-nums leading-tight text-slate-900 dark:text-white"
                              >
                                {{ formatAmount(row.amount) }}
                                <span
                                  class="text-[10px] font-medium text-slate-500 dark:text-slate-400"
                                >
                                  {{ t("currency_sar") || "SAR" }}
                                </span>
                              </div>
                              <div
                                class="mt-0.5 text-[10px] leading-tight tabular-nums text-slate-400 dark:text-slate-500"
                              >
                                <template v-if="offerDateLabel(row)">
                                  {{ offerDateLabel(row) }}
                                </template>
                                <template
                                  v-if="
                                    offerDateLabel(row) && offerTimeLabel(row)
                                  "
                                >
                                  ·
                                </template>
                                <template v-if="offerTimeLabel(row)">
                                  {{ offerTimeLabel(row) }}
                                </template>
                                <template
                                  v-if="
                                    !offerDateLabel(row) &&
                                    !offerTimeLabel(row)
                                  "
                                >
                                  —
                                </template>
                              </div>
                            </div>
                          </div>

                          <p
                            v-if="row.note"
                            class="mt-1.5 text-[10px] leading-relaxed break-words whitespace-pre-wrap text-slate-500 dark:text-slate-400 border-t border-slate-200/80 dark:border-slate-700/60 pt-1.5"
                          >
                            {{ row.note }}
                          </p>
                        </div>
                      </div>
                    </li>
                  </ul>
                </section>
              </div>
                </div>

                <!-- Accept: only while negotiation is open (status_id === 3) -->
                <div
                  v-if="isNegotiationOpen && canAccept && latestOtherOffer"
                  class="flex flex-col gap-2 px-4 py-2.5 border-t shrink-0 border-emerald-100 dark:border-emerald-900/40 bg-emerald-50/70 dark:bg-emerald-900/20"
                >
              <div class="flex items-center justify-between gap-3">
                <div
                  class="min-w-0 flex-1 text-xs sm:text-sm text-slate-700 dark:text-slate-200"
                >
                  <span class="font-medium">
                    {{
                      t("negotiation_accept_hint") ||
                      "Accept the latest offer from the other party"
                    }}
                  </span>
                  <span
                    class="ms-1 font-bold text-emerald-700 dark:text-emerald-300 whitespace-nowrap"
                  >
                    ({{ formatAmount(latestOtherOffer.amount) }}
                    {{ t("currency_sar") || "SAR" }})
                  </span>
                </div>
                <div class="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    class="inline-flex items-center justify-center gap-1 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
                    :disabled="isSubmitting || isRejecting"
                    @click="acceptOffer"
                  >
                    <i class="mdi mdi-check text-[14px]"></i>
                    {{
                      isSubmitting
                        ? t("processing") || "Processing..."
                        : t("accept_price") || "Accept Price"
                    }}
                  </button>

                  <button
                    type="button"
                    class="inline-flex items-center justify-center gap-1 px-4 py-2 text-xs font-semibold rounded-lg border border-rose-200 text-rose-600 bg-white hover:bg-rose-50 disabled:opacity-60 dark:border-rose-900/40 dark:text-rose-200 dark:bg-rose-900/30 dark:hover:bg-rose-900/50"
                    :disabled="isSubmitting || isRejecting"
                    @click="openRejectModal"
                  >
                    <i class="mdi mdi-close-circle-outline text-[14px]"></i>
                    {{
                      isRejecting
                        ? t("processing") || "Processing..."
                        : t("reject") || "Reject"
                    }}
                  </button>
                </div>
              </div>

              <div
                v-if="latestOtherOffer.note"
                class="rounded-lg border border-emerald-100 dark:border-emerald-900/50 bg-white/80 dark:bg-slate-900/50 px-3 py-2"
              >
                <div
                  class="text-[11px] font-semibold text-slate-700 dark:text-slate-200 mb-1"
                >
                  {{ t("negotiation_note_label") || "Note / details" }}
                </div>
                <p
                  class="text-xs leading-relaxed break-words whitespace-pre-wrap text-slate-600 dark:text-slate-300"
                >
                  {{ latestOtherOffer.note }}
                </p>
              </div>

              <p class="text-[11px] text-slate-500 dark:text-slate-400">
                {{
                  t("negotiation_reject_hint") ||
                  "If the offer does not suit you, you can reject the consultation."
                }}
              </p>
            </div>

                <!-- Rejected: negotiation ended without an agreement -->
                <div
                  v-else-if="isNegotiationRejected"
                  class="flex items-center gap-2 px-4 py-2.5 border-t shrink-0 border-rose-100 dark:border-rose-900/40 bg-rose-50/70 dark:bg-rose-900/20"
                >
              <span
                class="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-full border border-rose-300/50 dark:border-rose-800/50 bg-white/70 dark:bg-slate-900/40"
              >
                <i
                  class="mdi mdi-close-circle-outline text-rose-600 dark:text-rose-400 text-[16px]"
                ></i>
              </span>
              <p class="min-w-0 text-[10px] leading-relaxed text-slate-700 dark:text-slate-200">
                {{
                  t("negotiation_rejected") ||
                  "The consultation was rejected. Negotiation is closed."
                }}
              </p>
            </div>

              </div>
            </div>
          </section>
        </div>
      </main>
    </div>

    <!-- Offer modal -->
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showOfferModal"
        class="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:px-4 bg-slate-900/50 backdrop-blur-[2px]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="offer-modal-title"
        @click.self="closeOfferModal"
      >
        <div
          class="w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden bg-white border shadow-2xl dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-t-2xl sm:rounded-2xl"
        >
          <!-- Header -->
          <div
            class="flex items-start justify-between gap-3 px-5 py-4 border-b shrink-0 border-slate-100 dark:border-slate-800 bg-slate-50/90 dark:bg-slate-800/70"
          >
            <div class="flex items-start gap-3 min-w-0">
              <span
                class="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-primary/10"
              >
                <i
                  class="mdi mdi-file-document-edit-outline text-primary text-[20px]"
                ></i>
              </span>
              <div class="min-w-0">
                <h3
                  id="offer-modal-title"
                  class="text-sm font-semibold text-slate-900 dark:text-white"
                >
                  {{ t("negotiation_your_offer") || "Your offer" }}
                </h3>
                <p class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {{
                    t("negotiation_offer_modal_subtitle") ||
                    "Enter the amount and an optional note; both are sent together."
                  }}
                </p>
                <p
                  class="mt-1.5 text-[11px] leading-snug text-primary dark:text-teal-200"
                >
                  {{ negotiationLimitModalNote }}
                </p>
              </div>
            </div>

            <button
              type="button"
              class="inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:text-slate-200 disabled:opacity-60"
              :disabled="isSubmitting"
              :aria-label="t('close') || 'Close'"
              @click="closeOfferModal"
            >
              <i class="mdi mdi-close text-[18px]"></i>
            </button>
          </div>

          <!-- Body -->
          <form
            id="negotiation-offer-form"
            class="flex-1 px-5 py-4 space-y-4 overflow-y-auto custom-scrollbar"
            @submit.prevent="submitOffer"
          >
            <!-- Reference: what you are responding to -->
            <div
              v-if="latestOtherOffer"
              class="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700"
            >
              <span class="text-[11px] text-slate-500 dark:text-slate-400">
                {{
                  t("negotiation_latest_other_offer") ||
                  "Latest offer from the other party"
                }}
              </span>
              <span
                class="text-sm font-bold text-slate-800 dark:text-slate-100 whitespace-nowrap"
              >
                {{ formatAmount(latestOtherOffer.amount) }}
                {{ t("currency_sar") || "SAR" }}
              </span>
            </div>

            <!-- Amount -->
            <div class="flex flex-col gap-1.5">
              <label
                for="negotiation-offer-amount"
                class="text-[11px] font-medium text-slate-600 dark:text-slate-300"
              >
                {{
                  isConsultingAccount
                    ? t("offer_before_tax") || "Before tax"
                    : t("negotiation_amount_placeholder") || "Amount"
                }}
                <span class="text-rose-500">*</span>
              </label>
              <div class="relative">
                <input
                  id="negotiation-offer-amount"
                  ref="amountInputRef"
                  v-model="draftPrice"
                  type="text"
                  inputmode="numeric"
                  autocomplete="off"
                  class="w-full px-3 py-2.5 pe-14 text-sm bg-white border rounded-lg dark:bg-slate-950 text-slate-900 dark:text-white focus:outline-none focus:ring-2"
                  :class="
                    offerError
                      ? 'border-rose-300 dark:border-rose-800 focus:ring-rose-200 dark:focus:ring-rose-900'
                      : 'border-slate-200 dark:border-slate-700 focus:ring-primary/30'
                  "
                  :placeholder="
                    isConsultingAccount
                      ? t('offer_before_tax') || 'Before tax'
                      : t('negotiation_amount_placeholder') || 'Amount'
                  "
                  :disabled="isSubmitting"
                  :aria-invalid="!!offerError"
                  aria-describedby="negotiation-offer-amount-error"
                  @input="onPriceInput"
                />
                <span
                  class="pointer-events-none absolute inset-y-0 end-3 flex items-center text-[11px] font-medium text-slate-400"
                >
                  {{ t("currency_sar") || "SAR" }}
                </span>
              </div>
              <p
                v-if="offerError"
                id="negotiation-offer-amount-error"
                class="flex items-center gap-1 text-[11px] text-rose-600"
              >
                <i class="mdi mdi-alert-circle-outline text-[13px]"></i>
                {{ offerError }}
              </p>
            </div>

            <!-- Consultant: tax is added before send; show total that will be sent -->
            <div
              v-if="isConsultingAccount"
              class="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-950/50 px-3 py-2.5 space-y-2 text-xs"
            >
              <div class="flex items-center justify-between">
                <span class="text-slate-500">
                  {{ t("tax_percent") || "Tax %" }}
                </span>
                <span
                  class="font-semibold text-slate-800 dark:text-slate-100"
                >
                  {{ consultation.tax_percent }}%
                </span>
              </div>
              <div
                class="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-700"
              >
                <span class="text-slate-500">
                  {{ t("negotiation_price_sent") || "Amount sent (with tax)" }}
                </span>
                <span
                  class="text-sm font-bold text-slate-800 dark:text-slate-100"
                >
                  {{ formatAmount(draftPriceWithTax) }}
                  {{ t("currency_sar") || "SAR" }}
                </span>
              </div>
              <p class="text-[11px] text-slate-500 dark:text-slate-400">
                {{
                  t("negotiation_tax_added_hint") ||
                  "Tax will be added and the total will be sent to the client."
                }}
              </p>
            </div>

            <!-- Note -->
            <div class="flex flex-col gap-1.5">
              <div class="flex items-center justify-between gap-2">
                <label
                  for="negotiation-offer-note"
                  class="text-[11px] font-medium text-slate-600 dark:text-slate-300"
                >
                  {{ t("negotiation_note_label") || "Note / details" }}
                  <span class="font-normal text-slate-400">
                    ({{ t("optional") || "optional" }})
                  </span>
                </label>
                <span class="text-[10px] tabular-nums text-slate-400">
                  {{ draftNote.length }}/{{ noteMaxLength }}
                </span>
              </div>
              <textarea
                id="negotiation-offer-note"
                v-model="draftNote"
                rows="4"
                :maxlength="noteMaxLength"
                class="w-full min-h-[6rem] max-h-48 px-3 py-2 text-sm bg-white border rounded-lg resize-y dark:bg-slate-950 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                :placeholder="
                  t('negotiation_note_placeholder') ||
                  'Note / consultation details (optional)'
                "
                :disabled="isSubmitting"
              ></textarea>
            </div>
          </form>

          <!-- Footer -->
          <div
            class="flex items-center justify-end gap-2 px-5 py-3 border-t shrink-0 border-slate-100 dark:border-slate-800 bg-slate-50/90 dark:bg-slate-800/70"
          >
            <button
              type="button"
              class="px-4 py-2 text-xs font-medium bg-white border rounded-lg dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-60"
              :disabled="isSubmitting"
              @click="closeOfferModal"
            >
              {{ t("account_btn_cancel") || "Cancel" }}
            </button>
            <button
              type="submit"
              form="negotiation-offer-form"
              class="inline-flex items-center gap-1 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-60"
              :disabled="isSubmitting || !hasValidDraftPrice"
            >
              <i class="mdi mdi-send-outline text-[14px]"></i>
              {{
                isSubmitting
                  ? t("processing") || "Processing..."
                  : t("send_offer") || "Send Offer"
              }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Reject confirmation -->
    <div
      v-if="showRejectModal"
      class="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40"
      @click.self="closeRejectModal"
    >
      <div
        class="w-full max-w-md p-5 bg-white border shadow-xl dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-2xl"
      >
        <div class="flex items-start gap-3">
          <span
            class="inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-rose-50 dark:bg-rose-900/30"
          >
            <i class="mdi mdi-alert-outline text-rose-600 text-[20px]"></i>
          </span>
          <div class="min-w-0">
            <h3 class="text-sm font-semibold text-slate-900 dark:text-white">
              {{
                t("negotiation_reject_consultation") || "Reject consultation"
              }}
            </h3>
            <p class="mt-1 text-xs text-slate-600 dark:text-slate-300">
              {{
                t("negotiation_reject_confirm") ||
                "Are you sure you want to reject this consultation? The negotiation will be closed and cannot be reopened."
              }}
            </p>
          </div>
        </div>

        <div class="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            class="px-4 py-2 text-xs font-medium bg-white border rounded-lg dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-60"
            :disabled="isRejecting"
            @click="closeRejectModal"
          >
            {{ t("account_btn_cancel") || "Cancel" }}
          </button>
          <button
            type="button"
            class="inline-flex items-center gap-1 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-60"
            :disabled="isRejecting"
            @click="rejectConsultation"
          >
            <i class="mdi mdi-close-circle-outline text-[14px]"></i>
            {{
              isRejecting
                ? t("processing") || "Processing..."
                : t("confirm_reject") || "Confirm rejection"
            }}
          </button>
        </div>
      </div>
    </div>

    <Toast
      :show="toast.show"
      :type="toast.type"
      :message="toast.message"
      @update:show="toast.show = $event"
    />
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { useRouter, useRoute } from "vue-router";
import { messages } from "@/i18n";
import api from "@/services/api";
import AdminSidebar from "@/components/Auth/Sidebar.vue";
import AdminTopbar from "@/components/Auth/TopNav.vue";
import Toast from "@/components/Toast.vue";
import NegotiationHistorySkeleton from "@/components/consultations/NegotiationHistorySkeleton.vue";
import NegotiationSummarySkeleton from "@/components/consultations/NegotiationSummarySkeleton.vue";

/* ---------------- i18n ---------------- */
const initialLang = (
  document.documentElement.lang ||
  localStorage.getItem("app_lang") ||
  "ar"
).toLowerCase();

const lang = ref(initialLang.startsWith("ar") ? "ar" : "en");
function t(key) {
  return (messages[lang.value] && messages[lang.value][key]) || key;
}

const _langObserver = ref(null);
const _langHandler = (e) => {
  const next = (e.detail || "ar").toLowerCase().startsWith("en") ? "en" : "ar";
  if (next !== lang.value) lang.value = next;
};
window.addEventListener("app:lang", _langHandler, { passive: true });

_langObserver.value = new MutationObserver(() => {
  const htmlLang = (
    document.documentElement.lang ||
    localStorage.getItem("app_lang") ||
    "ar"
  ).toLowerCase();
  const next = htmlLang.startsWith("en") ? "en" : "ar";
  if (next !== lang.value) lang.value = next;
});
_langObserver.value.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ["lang", "dir"],
});

/* ---------------- layout ---------------- */
const isSidebarCollapsed = ref(false);
const isSidebarOpenMobile = ref(false);
const sidebarSearch = ref("");

function toggleSidebar() {
  if (window.innerWidth < 768)
    isSidebarOpenMobile.value = !isSidebarOpenMobile.value;
  else isSidebarCollapsed.value = !isSidebarCollapsed.value;
}

/* ---------------- router / auth ---------------- */
const router = useRouter();
const route = useRoute();
const id = route.params.id;

const user = ref(null);

function getCookie(name) {
  const match = document.cookie.match(
    new RegExp(
      "(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)",
    ),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function loadUserFromCookie() {
  const raw = getCookie("auth_user");
  if (!raw) return (user.value = null);
  try {
    user.value = JSON.parse(raw);
  } catch {
    user.value = null;
  }
}

function goBackToDetails() {
  router.push({ name: "consultations.show", params: { id } });
}

/* ---------------- toast ---------------- */
const toast = reactive({ show: false, type: "success", message: "" });
let toastTimer = null;

function showToast(message, type = "success") {
  toast.message = message;
  toast.type = type;
  toast.show = true;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => (toast.show = false), 2500);
}

/* ---------------- state ---------------- */
const isHistoryLoading = ref(false);
const hasHistoryLoadedOnce = ref(false);
const consultationLoaded = ref(false);
const consultationBootstrapReady = ref(false);
const isSubmitting = ref(false);
const isRejecting = ref(false);
const showRejectModal = ref(false);
const showOfferModal = ref(false);
const amountInputRef = ref(null);
const historyScrollRef = ref(null);
const offerError = ref("");
const noteMaxLength = 500;
const loadError = ref("");
const draftPrice = ref("");
const draftNote = ref("");
const negotiations = ref([]);
const consultation = reactive({
  number: "",
  subject: "",
  status: null,
  payment_status: null,
  offer_before_tax: 0,
  tax_percent: 15,
  negotiations_count: 0,
  can_negotiate: false,
  negotiation_round: 0,
  client_name: "",
  consultant_name: "",
  client_user_id: null,
  consultant_user_id: null,
});

const isConsultingAccount = computed(() => {
  const r = String(user.value?.role || "").toLowerCase();
  return r === "consultant" || r === "consultant-office";
});

const negotiationLimitNote = computed(() =>
  isConsultingAccount.value
    ? t("negotiation_limit_note") ||
      "Your opening price counts as attempt 1 of 3 available to you as the consultant. After that you cannot submit any other offer."
    : t("negotiation_limit_note_client") ||
      "You can send 3 negotiation offers only. After that you cannot submit any other offer.",
);

const negotiationLimitModalNote = computed(() =>
  isConsultingAccount.value
    ? t("negotiation_limit_modal_note") ||
      "Reminder: your opening price is attempt 1 of 3 for you as the consultant."
    : t("negotiation_limit_modal_note_client") ||
      "Reminder: you have 3 negotiation attempts only.",
);

const negotiationFinalRoundWarning = computed(() =>
  isConsultingAccount.value
    ? t("negotiation_final_round_warning_consultant") ||
      "This is your last allowed round — after this you must accept or reject, and no further offers can be submitted."
    : t("negotiation_final_round_warning_client") ||
      "This is your last allowed round — no further offers can be submitted.",
);

const draftPriceBeforeTax = computed(() =>
  Math.round(Number(draftPrice.value || 0)) || 0,
);

const draftPriceWithTax = computed(() => {
  const before = draftPriceBeforeTax.value;
  const taxP = Number(consultation.tax_percent || 0);
  return Math.round(before + (before * taxP) / 100);
});

const statusIdToValue = {
  1: "draft",
  2: "new",
  3: "under_study",
  4: "accepted",
  5: "finished",
};

/* ---------------- helpers ---------------- */
function pickLang(obj, field = "title") {
  if (!obj) return "";
  if (typeof obj === "string") return t(obj);
  const val = obj[field];
  if (val && typeof val === "object")
    return val[lang.value] || val.en || val.ar || "";
  return val || "";
}

function formatAmount(value) {
  const n = Math.round(Number(value || 0));
  if (!Number.isFinite(n)) return "0";
  return String(n);
}

function priceWithTax(beforeTax) {
  const before = Math.round(Number(beforeTax || 0));
  const taxP = Number(consultation.tax_percent || 0);
  return Math.round(before + (before * taxP) / 100);
}

function onPriceInput(e) {
  const cleaned = String(e?.target?.value ?? draftPrice.value ?? "").replace(
    /\D/g,
    "",
  );
  draftPrice.value = cleaned;
  if (offerError.value) offerError.value = "";
}

function formatDateTimeParts(raw) {
  if (!raw) return { dateLabel: "", timeLabel: "" };
  const str = String(raw);
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) {
    if (/[صم]|AM|PM|am|pm/.test(str) && str.length <= 12) {
      return { dateLabel: "", timeLabel: str };
    }
    return { dateLabel: str, timeLabel: "" };
  }
  // Depend on lang so labels refresh when language switches
  const locale = lang.value === "ar" ? "ar-SA" : "en-GB";
  try {
    return {
      dateLabel: d.toLocaleDateString(locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      timeLabel: d.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  } catch {
    return { dateLabel: str, timeLabel: "" };
  }
}

function offerDateLabel(row) {
  void lang.value;
  return formatDateTimeParts(row?.createdAt).dateLabel;
}

function offerTimeLabel(row) {
  void lang.value;
  return formatDateTimeParts(row?.createdAt).timeLabel;
}

function firstDefined(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return null;
}

function extractSenderId(item) {
  return firstDefined(
    item.user_id,
    item.sender_id,
    item.created_by_id,
    item.actor_id,
    item.proposed_by,
    item.offered_by,
    typeof item.created_by === "object"
      ? item.created_by?.user?.id ?? item.created_by?.id
      : item.created_by,
    item.user?.id,
    item.user?.user_id,
    item.sender?.id,
    item.sender?.user_id,
    item.creator?.id,
    item.creator?.user_id,
  );
}

function extractSenderName(item) {
  return (
    firstDefined(
      item.sender_name,
      item.user_name,
      item.name,
      item.sender?.name,
      item.sender?.full_name,
      item.user?.name,
      item.user?.full_name,
      item.created_by?.user?.name,
      item.created_by?.name,
      item.creator?.name,
      typeof item.sender === "string" ? item.sender : null,
    ) || ""
  );
}

function classifyRoleHint(raw) {
  const s = String(raw || "").toLowerCase();
  if (!s) return null;
  if (
    s.includes("consultant") ||
    s.includes("مستشار") ||
    s.includes("office") ||
    s === "consultant-office"
  ) {
    return "consultant";
  }
  if (s.includes("client") || s.includes("عميل") || s.includes("requester")) {
    return "client";
  }
  return null;
}

function resolveSenderMeta(item) {
  const senderId = extractSenderId(item);
  const roleHint = classifyRoleHint(
    firstDefined(
      item.role,
      item.actor,
      item.by,
      item.party,
      item.sender_type,
      item.user_type,
      item.creator_type,
      item.created_by_type,
      item.model_type,
      item.userable_type,
      item.created_by_role,
      item.user?.role,
      item.sender?.role,
      item.created_by?.role,
      item.creator?.role,
      item.proposer_type,
      item.offered_by_type,
    ),
  );

  const clientId = consultation.client_user_id;
  const consultantId = consultation.consultant_user_id;
  const myId = user.value?.id ?? user.value?.user_id ?? null;
  const myRole = String(user.value?.role || "").toLowerCase();
  const iAmConsultant =
    myRole === "consultant" || myRole === "consultant-office";
  const iAmClient = myRole === "client";

  const apiIsConsultant = firstDefined(
    item.is_consultant,
    item.by_consultant,
    item.consultant_offer,
    item.is_consulting_offer,
    item.is_consultant_offer,
  );
  const apiIsClient = firstDefined(
    item.is_client,
    item.by_client,
    item.client_offer,
    item.is_client_offer,
  );
  const apiIsMine = firstDefined(
    item.is_mine,
    item.is_owner,
    item.is_current_user,
    item.is_my_offer,
  );

  let isConsultant = null;

  if (apiIsConsultant != null) {
    isConsultant = !!apiIsConsultant;
  } else if (apiIsClient != null) {
    isConsultant = !apiIsClient;
  } else if (
    senderId != null &&
    consultantId != null &&
    String(senderId) === String(consultantId)
  ) {
    isConsultant = true;
  } else if (
    senderId != null &&
    clientId != null &&
    String(senderId) === String(clientId)
  ) {
    isConsultant = false;
  } else if (roleHint === "consultant") {
    isConsultant = true;
  } else if (roleHint === "client") {
    isConsultant = false;
  } else if (
    senderId != null &&
    myId != null &&
    String(senderId) === String(myId)
  ) {
    isConsultant = iAmConsultant;
  } else if (
    senderId != null &&
    myId != null &&
    String(senderId) !== String(myId)
  ) {
    isConsultant = iAmClient ? true : iAmConsultant ? false : null;
  }

  if (isConsultant == null) isConsultant = false;

  let isMine = null;
  if (apiIsMine != null) {
    isMine = !!apiIsMine;
  } else if (senderId != null && myId != null) {
    isMine = String(senderId) === String(myId);
  } else {
    isMine =
      (iAmConsultant && isConsultant) || (iAmClient && !isConsultant);
  }

  const apiName = String(extractSenderName(item) || "").trim();
  const fallbackName = isConsultant
    ? consultation.consultant_name
    : consultation.client_name;
  const senderName =
    apiName ||
    fallbackName ||
    (isConsultant
      ? t("role_consultant") || "Consultant"
      : t("role_client") || "Client");

  const roleLabel = isConsultant
    ? t("role_consultant") || "Consultant"
    : t("role_client") || "Client";

  return { isConsultant, isMine, senderName, roleLabel };
}

function normalizeNegotiationList(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload.negotiations)) return payload.negotiations;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.items)) return payload.items;
  return [];
}

function getNegotiationTimestamp(row) {
  const raw = row?.raw || row;
  const value =
    row?.createdAt ||
    raw?.created_at ||
    raw?.updated_at ||
    raw?.offered_at ||
    "";
  const ms = Date.parse(String(value));
  return Number.isFinite(ms) ? ms : 0;
}

/** Keep API sender order; normalize only when API returns newest-first. */
function sortNegotiationsChronologically(rows) {
  return [...rows].sort((a, b) => {
    const ta = getNegotiationTimestamp(a);
    const tb = getNegotiationTimestamp(b);
    if (ta && tb && ta !== tb) return ta - tb;
    return (a.apiIndex ?? 0) - (b.apiIndex ?? 0);
  });
}

function buildConsultantSeedDisplayRow() {
  const amount = Math.round(Number(consultation.offer_before_tax || 0));
  if (!amount) return null;

  return {
    id: "consultation-initial-offer",
    amount,
    createdAt: "",
    note: "",
    isConsultant: true,
    isMine: isConsultingAccount.value,
    senderName:
      consultation.consultant_name ||
      t("role_consultant") ||
      "Consultant",
    roleLabel: t("role_consultant") || "Consultant",
    apiIndex: -1,
    isConsultationSeed: true,
    raw: {},
  };
}

/** Show consultation offer before client counter when API list lacks consultant row. */
function ensureConsultantSeedInList(rows) {
  if (!rows.length) return rows;
  if (rows.some((r) => r.isConsultant)) return rows;

  const seed = buildConsultantSeedDisplayRow();
  if (!seed) return rows;

  return [seed, ...rows];
}

function orderRoundPair(pair) {
  return [...pair].sort((a, b) => {
    if (a.isConsultant !== b.isConsultant) {
      return a.isConsultant ? -1 : 1;
    }
    const ta = getNegotiationTimestamp(a);
    const tb = getNegotiationTimestamp(b);
    if (ta && tb && ta !== tb) return ta - tb;
    return (a.apiIndex ?? 0) - (b.apiIndex ?? 0);
  });
}

/** Stable display order: chronological rounds, consultant first within each round. */
function normalizeNegotiationsForDisplay(rows) {
  if (!rows.length) return rows;

  let sorted = sortNegotiationsChronologically(rows);
  sorted = ensureConsultantSeedInList(sorted);

  const result = [];
  for (let i = 0; i < sorted.length; i += 2) {
    result.push(...orderRoundPair(sorted.slice(i, i + 2)));
  }
  return result;
}

function mapNegotiationRow(item, index) {
  const amount = Math.round(
    Number(
      item.price ?? item.amount ?? item.offer ?? item.value ?? item.offer_amount ?? 0,
    ) || 0,
  );
  const createdAt =
    item.created_at ||
    item.date ||
    item.time ||
    item.datetime ||
    item.offered_at ||
    "";
  const note = String(item.note || item.comment || item.message || "").trim();
  const meta = resolveSenderMeta(item);

  return {
    id: item.id ?? `neg-${index}`,
    amount,
    createdAt,
    note,
    isConsultant: meta.isConsultant,
    isMine: meta.isMine,
    senderName: meta.senderName,
    roleLabel: meta.roleLabel,
    apiIndex: index,
    raw: item,
  };
}

/* ---------------- status badge ---------------- */
const statusValue = computed(() => {
  const s = consultation.status;
  if (!s) return "";
  const raw =
    s?.value ||
    s?.slug ||
    s?.key ||
    s?.code ||
    s?.name ||
    s?.title?.en ||
    s?.title?.ar ||
    (typeof s === "number" ? statusIdToValue[Number(s)] : null) ||
    (s?.id != null ? statusIdToValue[Number(s.id)] : null) ||
    s;
  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-+/g, "_");
});

const displayStatusValue = computed(() => {
  const baseStatusId = Number(consultation.status?.id ?? 0);
  if (
    baseStatusId === 4 &&
    String(consultation.payment_status || "").toLowerCase() === "under_review"
  ) {
    return "under_review";
  }
  return statusValue.value;
});

const displayStatusText = computed(() => {
  const baseStatusId = Number(consultation.status?.id ?? 0);
  const pay = String(consultation.payment_status || "").toLowerCase();
  if (baseStatusId === 4 && pay === "under_review") {
    return t("consult_status_under_review") || "Under Review";
  }
  return pickLang(consultation.status, "title") || "";
});

const statusChipClass = computed(() => {
  switch (displayStatusValue.value) {
    case "under_review":
      return "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300";
    case "accepted":
    case "answered":
      return "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300";
    case "rejected":
    case "closed":
      return "bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300";
    case "in_progress":
    case "open":
    case "under_study":
      return "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300";
    default:
      return "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200";
  }
});

const currentOffer = computed(() => {
  const latest = latestOffer.value;
  if (latest) return latest.amount;
  return priceWithTax(consultation.offer_before_tax);
});

const consultationStatusId = computed(() =>
  Number(consultation.status?.id ?? 0),
);

/** Negotiation actions are allowed only while status_id === 3 (under study). */
const isNegotiationOpen = computed(() => consultationStatusId.value === 3);

const isNegotiationRejected = computed(
  () => statusValue.value === "rejected" || consultationStatusId.value === 7,
);

const isNegotiationSettled = computed(
  () =>
    !isNegotiationOpen.value &&
    !isNegotiationRejected.value &&
    consultationStatusId.value > 0 &&
    Number(consultation.negotiations_count || negotiations.value.length) > 0,
);

/** Page may be opened to negotiate (status 3) or to view past negotiations.
 * Consultants cannot open until the client has started (negotiations_count > 0).
 */
const canAccessNegotiationPage = computed(() => {
  const statusId = consultationStatusId.value;
  const count = Number(consultation.negotiations_count || 0);
  if (statusId === 3) {
    if (isConsultingAccount.value) return count > 0;
    return true;
  }
  return count > 0;
});

function redirectIfNegotiationInaccessible() {
  if (!canAccessNegotiationPage.value) {
    router.replace({ name: "consultations.show", params: { id } });
    return true;
  }
  return false;
}

const latestOffer = computed(() => {
  const list = negotiations.value;
  return list.length ? list[list.length - 1] : null;
});

const latestOtherOffer = computed(() => {
  const latest = latestOffer.value;
  if (latest && !latest.isMine) return latest;
  return null;
});

/** Each round = consultant offer + client counter-offer (2 entries in history). */
const MAX_NEGOTIATION_ROUNDS = 3;
const MAX_PARTY_ATTEMPTS = 3;
const NEGOTIATION_ROUND_STEPS = [1, 2, 3];

function roundFromOfferCount(count) {
  const n = Number(count || 0);
  if (n <= 0) return 1;
  return Math.min(MAX_NEGOTIATION_ROUNDS, Math.ceil(n / 2));
}

/** Prefer the live log. API `negotiation_round` often lags behind new offers. */
const currentNegotiationRound = computed(() => {
  if (negotiations.value.length > 0) {
    return roundFromOfferCount(negotiations.value.length);
  }
  const fromCount = Number(consultation.negotiations_count || 0);
  if (fromCount > 0) return roundFromOfferCount(fromCount);
  const apiRound = Number(consultation.negotiation_round || 0);
  if (apiRound > 0) return Math.min(MAX_NEGOTIATION_ROUNDS, apiRound);
  return 1;
});

const isFinalRound = computed(
  () => isNegotiationOpen.value && currentNegotiationRound.value === MAX_NEGOTIATION_ROUNDS,
);

/** Only while this user is writing their last personal offer — not after sending, not on accept/reject. */
const showFinalRoundWarning = computed(() => {
  if (!isNegotiationOpen.value || hasReachedMyMaxOffers.value) return false;
  if (!canShowOfferButton.value) return false;
  if (isConsultingAccount.value) {
    return consultantAttemptCount.value === MAX_PARTY_ATTEMPTS - 1;
  }
  return clientAttemptCount.value === MAX_PARTY_ATTEMPTS - 1;
});

/** Shared log length for grouping only — not used to cap the client. */
const negotiationOfferCount = computed(() => {
  const fromList = negotiations.value.length;
  const fromConsultation = Number(consultation.negotiations_count || 0);
  return Math.max(fromList, fromConsultation);
});

const clientAttemptCount = computed(
  () => negotiations.value.filter((row) => !row.isConsultant).length,
);

/** Opening consultation price counts as consultant attempt 1; extra consultant rows are attempts 2–3. */
const consultantAttemptCount = computed(() => {
  const openingBefore = Math.round(Number(consultation.offer_before_tax || 0));
  const opening = openingBefore > 0 ? 1 : 0;
  const apiConsultantRows = negotiations.value.filter(
    (row) => row.isConsultant && !row.isConsultationSeed,
  );
  const openingWithTax = openingBefore ? priceWithTax(openingBefore) : 0;
  const firstIsOpening =
    apiConsultantRows.length > 0 &&
    (apiConsultantRows[0].amount === openingBefore ||
      apiConsultantRows[0].amount === openingWithTax);
  const extra = firstIsOpening
    ? apiConsultantRows.length - 1
    : apiConsultantRows.length;
  return opening + extra;
});

const historySkeletonCount = computed(() => {
  const n = Number(consultation.negotiations_count || 0);
  if (n > 0) return Math.min(n, 4);
  return 2;
});

const hasReachedMyMaxOffers = computed(() =>
  isConsultingAccount.value
    ? consultantAttemptCount.value >= MAX_PARTY_ATTEMPTS
    : clientAttemptCount.value >= MAX_PARTY_ATTEMPTS,
);

const hasReachedMaxOffers = computed(() => hasReachedMyMaxOffers.value);

/** History grouped by round (consultant offer first within each round). */
const negotiationRoundGroups = computed(() => {
  const list = negotiations.value;
  if (!list.length) return [];

  const groups = [];
  for (let i = 0; i < list.length; i += 2) {
    const round = Math.floor(i / 2) + 1;
    groups.push({ round, offers: list.slice(i, i + 2) });
  }
  return groups;
});

function scrollHistoryToLastOffer() {
  nextTick(() => {
    requestAnimationFrame(() => {
      const el = historyScrollRef.value;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    });
  });
}

function formatRoundLabel(round) {
  if (lang.value === "ar") {
    return `${t("negotiation_round_of") || "الجولة"} ${round}`;
  }
  return `${t("negotiation_round_of") || "Round"} ${round}`;
}

function roundStepClass(step) {
  if (step === currentNegotiationRound.value) {
    return "bg-primary text-white shadow-sm ring-2 ring-primary/30";
  }
  if (step < currentNegotiationRound.value) {
    return "bg-primary/15 text-primary border border-primary/25";
  }
  return "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500 border border-slate-200 dark:border-slate-700";
}

const latestOfferAuthorLabel = computed(() => {
  const latest = latestOffer.value;
  if (!latest) return "";
  return latest.isConsultant
    ? t("role_consultant") || "Consultant"
    : t("role_client") || "Client";
});

const turnBadgeLabel = computed(() => {
  if (!isNegotiationOpen.value) return "";
  if (canSubmitOffer.value) {
    return isConsultingAccount.value
      ? t("negotiation_consultant_turn") || "Consultant's turn"
      : t("negotiation_client_turn") || "Client's turn";
  }
  if (isWaitingForOtherParty.value) {
    return isConsultingAccount.value
      ? t("negotiation_client_turn") || "Client's turn"
      : t("negotiation_consultant_turn") || "Consultant's turn";
  }
  if (canAccept.value) {
    return isConsultingAccount.value
      ? t("negotiation_consultant_turn") || "Consultant's turn"
      : t("negotiation_client_turn") || "Client's turn";
  }
  return "";
});

/** Consultation fields known from bootstrap or API — before history loads. */
const consultationReady = computed(
  () => consultationLoaded.value || consultationBootstrapReady.value,
);

function applyNegotiationBootstrap() {
  const boot = history.state?.negotiationBootstrap;
  if (!boot || String(boot.consultationId) !== String(id)) return;

  consultation.can_negotiate = !!boot.can_negotiate;
  consultation.negotiations_count = Number(boot.negotiations_count ?? 0);
  consultation.offer_before_tax = Number(boot.offer_before_tax ?? 0);
  if (boot.tax_percent != null) {
    consultation.tax_percent = Number(boot.tax_percent);
  }
  if (boot.subject) consultation.subject = boot.subject;
  if (boot.number) consultation.number = String(boot.number).trim();
  if (boot.statusId != null) {
    consultation.status = { id: Number(boot.statusId) };
  }
  consultation.negotiation_round = Number(
    boot.negotiation_round ??
      (consultation.negotiations_count > 0
        ? roundFromOfferCount(consultation.negotiations_count)
        : 0),
  );
  consultationBootstrapReady.value = true;
}

applyNegotiationBootstrap();

/** Whose turn it is to send the next offer. */
const isMyTurnToOffer = computed(() => {
  if (!consultationReady.value) return false;
  if (!isNegotiationOpen.value) return false;
  if (hasReachedMaxOffers.value) return false;

  const latest = latestOffer.value;
  if (latest) {
    return !latest.isMine;
  }

  return consultation.can_negotiate;
});

/** Decide visibility from consultation (bootstrap/API) before history loads. */
const canShowOfferButton = computed(() => {
  if (!consultationReady.value) return false;
  if (isNegotiationSettled.value || isNegotiationRejected.value) return false;
  return isMyTurnToOffer.value;
});

/** Backend `can_negotiate` + latest offer sender when history is available. */
const canSubmitOffer = computed(() => {
  if (!consultationLoaded.value) return false;
  return canShowOfferButton.value;
});

const hasValidDraftPrice = computed(() => draftPriceBeforeTax.value >= 1);

/** Final-round CTA from consultation fields — no history fetch required. */
const isFinalOfferCta = computed(() => showFinalRoundWarning.value);

const offerCtaLabel = computed(() => {
  if (isFinalOfferCta.value) {
    return t("negotiation_last_offer") || "Send final offer";
  }
  const hasOtherPartyOffer =
    latestOtherOffer.value ||
    (isMyTurnToOffer.value && negotiationOfferCount.value > 0);
  return hasOtherPartyOffer
    ? t("negotiation_counter_offer") || "Send counter-offer"
    : t("negotiation_make_offer") || "Make an offer";
});

const isWaitingForOtherParty = computed(() => {
  if (!isNegotiationOpen.value) return false;

  const latest = latestOffer.value;
  if (latest) return latest.isMine;

  if (!negotiations.value.length && negotiationOfferCount.value > 0) {
    return !consultation.can_negotiate;
  }

  return false;
});

const canAccept = computed(
  () => isNegotiationOpen.value && !!latestOtherOffer.value,
);

/** Last-turn only: the party who must accept/reject, not the one who sent the last offer. */
const showMaxOffersHint = computed(
  () =>
    hasReachedMaxOffers.value &&
    isNegotiationOpen.value &&
    canAccept.value,
);

/** Either party may end the consultation while the negotiation is still open. */
const canRejectConsultation = computed(() => isNegotiationOpen.value);

const OFFER_POLL_MS = 5000;
const REPLY_POLL_MS = 12000;

let consultationEtag = "";
let negotiationsEtag = "";

function httpNotModified(status) {
  return (status >= 200 && status < 300) || status === 304;
}

function readEtag(res) {
  const headers = res?.headers || {};
  return headers.etag || headers.ETag || "";
}

async function refreshNegotiationState({
  silent = true,
  scrollHistory = false,
  includeShow = false,
} = {}) {
  if (includeShow) {
    await fetchConsultation();
  }
  await fetchNegotiations({
    silent,
    scrollToEnd: scrollHistory,
    conditional: silent && !includeShow,
  });
  redirectIfNegotiationInaccessible();
}

/* ---------------- API ---------------- */
function applyConsultationPayload(c) {
  if (!c || typeof c !== "object") return;

  const handledBy = c.handled_by || null;
  const requester = c.created_by || null;
  const requesterUser = requester?.user || null;
  const handledUser = handledBy?.user || null;

  consultation.number = String(
    c.consultation_number || c.number || consultation.number || "",
  ).trim();
  if (c.subject != null) consultation.subject = c.subject || "";
  if (c.status != null) consultation.status = c.status;
  if (c.payment_status != null) consultation.payment_status = c.payment_status;

  const price =
    c.offer_before_tax ??
    c.offer_price_before_tax ??
    c.offer_price ??
    c.price;
  if (price != null) consultation.offer_before_tax = Number(price || 0);
  if (c.tax_percent != null) consultation.tax_percent = Number(c.tax_percent);

  if (c.negotiations_count != null) {
    consultation.negotiations_count = Number(c.negotiations_count ?? 0);
  }
  if (c.can_negotiate != null) consultation.can_negotiate = !!c.can_negotiate;
  if (c.negotiation_round != null || c.current_negotiation_round != null) {
    consultation.negotiation_round = Number(
      c.negotiation_round ?? c.current_negotiation_round ?? 0,
    );
  }

  consultation.client_name =
    c.client_name ||
    c.requester_name ||
    requesterUser?.name ||
    requesterUser?.full_name ||
    requester?.name ||
    requester?.full_name ||
    requester?.manager_name ||
    consultation.client_name ||
    "";

  consultation.consultant_name =
    handledUser?.name ||
    handledUser?.full_name ||
    handledBy?.name ||
    handledBy?.full_name ||
    c.consultant_name ||
    consultation.consultant_name ||
    "";

  consultation.client_user_id =
    requesterUser?.id ??
    c.requester_user_id ??
    c.created_by_user_id ??
    requester?.id ??
    consultation.client_user_id ??
    null;

  consultation.consultant_user_id =
    handledUser?.id ??
    handledBy?.user_id ??
    handledBy?.id ??
    c.consultant_user_id ??
    consultation.consultant_user_id ??
    null;
}

function applyNegotiationsPayload(payload, { scrollToEnd = false } = {}) {
  const root =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? payload
      : {};
  if (root.consultation) applyConsultationPayload(root.consultation);
  if (root.can_negotiate != null) consultation.can_negotiate = !!root.can_negotiate;
  if (root.negotiations_count != null) {
    consultation.negotiations_count = Number(root.negotiations_count ?? 0);
  }
  if (root.negotiation_round != null) {
    consultation.negotiation_round = Number(root.negotiation_round ?? 0);
  }

  const list = normalizeNegotiationList(payload);
  const prevLen = negotiations.value.length;
  const prevLatestId = negotiations.value.at(-1)?.id;
  negotiations.value = normalizeNegotiationsForDisplay(
    list.map((item, index) => mapNegotiationRow(item, index)),
  );
  hasHistoryLoadedOnce.value = true;

  const latestChanged = negotiations.value.at(-1)?.id !== prevLatestId;
  if (scrollToEnd || negotiations.value.length > prevLen || latestChanged) {
    scrollHistoryToLastOffer();
  }
}

async function fetchConsultation({ hydrateNegotiations = false } = {}) {
  try {
    const headers = {};
    if (consultationEtag) headers["If-None-Match"] = consultationEtag;

    const res = await api.get(`/consultations/${id}`, {
      headers,
      validateStatus: httpNotModified,
    });

    if (res.status === 304) {
      consultationLoaded.value = true;
      return { notModified: true };
    }

    const tag = readEtag(res);
    if (tag) consultationEtag = tag;

    const body = res.data?.data ?? res.data ?? {};
    const c =
      body.consultation && typeof body.consultation === "object"
        ? { ...body, ...body.consultation }
        : body;
    applyConsultationPayload(c);

    if (hydrateNegotiations) {
      const list = normalizeNegotiationList(c);
      const hasEmbeddedList = Array.isArray(c.negotiations) || list.length > 0;
      if (hasEmbeddedList) {
        applyNegotiationsPayload(c, { scrollToEnd: true });
      } else if (c.latest_negotiation) {
        applyNegotiationsPayload(
          { negotiations: [c.latest_negotiation], ...c },
          { scrollToEnd: true },
        );
      }
    }
  } catch (e) {
    console.error(e);
  } finally {
    consultationLoaded.value = true;
  }
}

async function fetchNegotiations({
  silent = false,
  scrollToEnd = false,
  conditional = false,
} = {}) {
  if (!silent) {
    isHistoryLoading.value = true;
    loadError.value = "";
  }
  try {
    const headers = {};
    if (conditional && negotiationsEtag) {
      headers["If-None-Match"] = negotiationsEtag;
    }

    const res = await api.get(`/consultations/${id}/negotiations`, {
      headers,
      validateStatus: httpNotModified,
    });

    if (res.status === 304) return { notModified: true };

    const tag = readEtag(res);
    if (tag) negotiationsEtag = tag;

    applyNegotiationsPayload(res.data?.data ?? res.data, { scrollToEnd });
  } catch (e) {
    console.error(e);
    if (!silent) {
      loadError.value =
        e?.response?.data?.message ||
        t("negotiation_load_failed") ||
        "Failed to load negotiations.";
      negotiations.value = [];
    }
  } finally {
    if (!silent) {
      isHistoryLoading.value = false;
      hasHistoryLoadedOnce.value = true;
    }
  }
}

function retryLoadHistory() {
  loadError.value = "";
  fetchNegotiations({ silent: false, scrollToEnd: true });
}

function appendOptimisticOffer(price, note) {
  const row = {
    id: `optimistic-${Date.now()}`,
    amount: Math.round(Number(price) || 0),
    createdAt: new Date().toISOString(),
    note: String(note || "").trim(),
    isConsultant: isConsultingAccount.value,
    isMine: true,
    senderName: t("you") || "You",
    roleLabel: isConsultingAccount.value
      ? t("role_consultant") || "Consultant"
      : t("role_client") || "Client",
    apiIndex: negotiations.value.length,
    raw: {},
  };
  negotiations.value = normalizeNegotiationsForDisplay([
    ...negotiations.value,
    row,
  ]);
  consultation.negotiations_count = Math.max(
    Number(consultation.negotiations_count || 0),
    negotiations.value.length,
  );
}

function openOfferModal() {
  if (!canSubmitOffer.value) return;
  offerError.value = "";
  showOfferModal.value = true;
  nextTick(() => amountInputRef.value?.focus());
}

function closeOfferModal() {
  if (isSubmitting.value) return;
  showOfferModal.value = false;
}

async function submitOffer() {
  if (!canSubmitOffer.value) return;
  const beforeTax = Math.round(Number(draftPrice.value));
  if (!Number.isFinite(beforeTax) || beforeTax < 1) {
    offerError.value =
      t("negotiation_amount_required") || "Please enter a valid amount.";
    nextTick(() => amountInputRef.value?.focus());
    return;
  }
  if (hasReachedMaxOffers.value) {
    showToast(
      t("negotiation_max_offers_hint") ||
        "Maximum offers reached. Please accept or reject the latest offer.",
      "error",
    );
    return;
  }
  if (isSubmitting.value) return;

  // Consultant: add tax before sending so client sees the final price
  const price = isConsultingAccount.value
    ? priceWithTax(beforeTax)
    : beforeTax;

  const note = String(draftNote.value || "").trim();
  const payload = { price };
  if (note) payload.note = note;

  isSubmitting.value = true;
  try {
    await api.post(`/consultations/${id}/negotiations`, payload);
    draftPrice.value = "";
    draftNote.value = "";
    offerError.value = "";
    showOfferModal.value = false;
    showToast(
      t("negotiation_offer_success") || "Offer submitted successfully.",
      "success",
    );
    appendOptimisticOffer(price, note);
    negotiationsEtag = "";
    await refreshNegotiationState({ silent: true, scrollHistory: true });
  } catch (e) {
    const msg =
      e?.response?.data?.message ||
      Object.values(e?.response?.data?.errors || {}).flat()?.[0] ||
      t("negotiation_offer_failed") ||
      "Failed to submit offer.";
    showToast(msg, "error");
  } finally {
    isSubmitting.value = false;
  }
}

async function acceptOffer() {
  if (!canAccept.value || isSubmitting.value) return;

  isSubmitting.value = true;
  try {
    await api.post(`/consultations/${id}/negotiations/accept`, {});

    // Optimistic UI: close negotiation actions immediately (status_id !== 3)
    consultation.status = {
      ...(consultation.status && typeof consultation.status === "object"
        ? consultation.status
        : {}),
      id: 4,
      value: "accepted",
    };
    draftPrice.value = "";
    draftNote.value = "";

    showToast(
      t("negotiation_accept_success") || "Price accepted successfully.",
      "success",
    );
    negotiationsEtag = "";
    consultationEtag = "";
    await refreshNegotiationState({ silent: true, includeShow: true });
  } catch (e) {
    const msg =
      e?.response?.data?.message ||
      Object.values(e?.response?.data?.errors || {}).flat()?.[0] ||
      t("negotiation_accept_failed") ||
      "Failed to accept price.";
    showToast(msg, "error");
  } finally {
    isSubmitting.value = false;
  }
}

function openRejectModal() {
  if (!canRejectConsultation.value) return;
  showRejectModal.value = true;
}

function closeRejectModal() {
  if (isRejecting.value) return;
  showRejectModal.value = false;
}

async function rejectConsultation() {
  if (!canRejectConsultation.value || isRejecting.value) return;

  isRejecting.value = true;
  try {
    await api.post(`/consultations/${id}/negotiation/reject`, {});

    // Optimistic UI: close negotiation actions immediately (status_id !== 3)
    consultation.status = {
      ...(consultation.status && typeof consultation.status === "object"
        ? consultation.status
        : {}),
      id: 7,
      value: "rejected",
      title: { ar: "مرفوضة", en: "Rejected" },
    };
    draftPrice.value = "";
    draftNote.value = "";
    showRejectModal.value = false;

    showToast(
      t("negotiation_reject_success") || "The consultation has been rejected.",
      "success",
    );
    negotiationsEtag = "";
    consultationEtag = "";
    await refreshNegotiationState({ silent: true, includeShow: true });
  } catch (e) {
    const msg =
      e?.response?.data?.message ||
      Object.values(e?.response?.data?.errors || {}).flat()?.[0] ||
      t("negotiation_reject_failed") ||
      "Failed to reject the consultation.";
    showToast(msg, "error");
  } finally {
    isRejecting.value = false;
  }
}

/* ---------------- modal behaviour ---------------- */
const isAnyModalOpen = computed(
  () => showOfferModal.value || showRejectModal.value,
);

// Polling can end your turn while the modal is open; don't leave a dead form up
watch(canSubmitOffer, (allowed) => {
  if (!allowed && !isSubmitting.value) showOfferModal.value = false;
});

watch(isAnyModalOpen, (open) => {
  document.body.style.overflow = open ? "hidden" : "";
});

function onModalKeydown(e) {
  if (e.key !== "Escape") return;
  if (showOfferModal.value) closeOfferModal();
  else if (showRejectModal.value) closeRejectModal();
}

/* ---------------- lifecycle ---------------- */
let pollTimer = null;
let pollInFlight = false;

function pollDelayMs() {
  if (isWaitingForOtherParty.value) return REPLY_POLL_MS;
  return OFFER_POLL_MS;
}

function stopOfferPoll() {
  if (pollTimer) {
    window.clearTimeout(pollTimer);
    pollTimer = null;
  }
}

function scheduleOfferPoll() {
  stopOfferPoll();
  if (!isNegotiationOpen.value) return;
  if (document.visibilityState !== "visible") return;
  pollTimer = window.setTimeout(runOfferPoll, pollDelayMs());
}

async function runOfferPoll() {
  if (document.visibilityState !== "visible" || !isNegotiationOpen.value) {
    return;
  }
  if (pollInFlight) {
    scheduleOfferPoll();
    return;
  }
  pollInFlight = true;
  try {
    await fetchNegotiations({ silent: true, conditional: true });
  } finally {
    pollInFlight = false;
    scheduleOfferPoll();
  }
}

function onVisibilityChange() {
  if (document.visibilityState === "hidden") {
    stopOfferPoll();
    return;
  }
  runOfferPoll();
}

onMounted(async () => {
  loadUserFromCookie();
  document.addEventListener("keydown", onModalKeydown);
  document.addEventListener("visibilitychange", onVisibilityChange);

  await fetchConsultation({ hydrateNegotiations: true });
  if (
    !hasHistoryLoadedOnce.value ||
    (Number(consultation.negotiations_count || 0) > 0 &&
      !negotiations.value.length)
  ) {
    await fetchNegotiations({ silent: false, scrollToEnd: true });
  } else {
    isHistoryLoading.value = false;
  }

  if (redirectIfNegotiationInaccessible()) return;
  scheduleOfferPoll();
});

watch(isNegotiationOpen, (open) => {
  if (open) scheduleOfferPoll();
  else stopOfferPoll();
});

onBeforeUnmount(() => {
  if (_langObserver.value) _langObserver.value.disconnect();
  window.removeEventListener("app:lang", _langHandler);
  document.removeEventListener("keydown", onModalKeydown);
  document.removeEventListener("visibilitychange", onVisibilityChange);
  document.body.style.overflow = "";
  if (toastTimer) clearTimeout(toastTimer);
  stopOfferPoll();
});
</script>
